import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity,
  Loader2,
  RefreshCw,
  Search,
  Trash2,
  Calendar,
  CheckCheck,
  ShoppingCart,
  CreditCard,
  UserPlus,
  Package,
  Trash,
  Edit3,
  AlertTriangle,
  FileText,
  RotateCcw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtTime = (iso) => (iso || "").slice(0, 16).replace("T", " ");
const fmtDay = (iso) => (iso || "").slice(0, 10);

// Choose icon + color for each activity action / resource_type.
const styleFor = (entry) => {
  const a = entry.action || "";
  const r = entry.resource_type || "";
  if (r === "user_signup" || a === "signup") return { Icon: UserPlus, color: "#10B981", bg: "#ECFDF5" };
  if (r === "order" && a === "create") return { Icon: ShoppingCart, color: "#3B82F6", bg: "#EFF6FF" };
  if (r === "order" && (a === "update" || a === "edit")) return { Icon: Edit3, color: "#F59E0B", bg: "#FFF8EC" };
  if (r === "order" && a === "delete") return { Icon: Trash, color: "#E85D45", bg: "#FEF2F2" };
  if (r === "payment_voucher" || r === "supplier_voucher") return { Icon: CreditCard, color: "#7C3AED", bg: "#F5F3FF" };
  if (r === "cash" || a === "deposit_capital") return { Icon: CreditCard, color: "#0891B2", bg: "#ECFEFF" };
  if (r === "product" || r === "purchase_invoice") return { Icon: Package, color: "#1E3A5F", bg: "#E0F2FE" };
  if (r === "damaged_item") return { Icon: AlertTriangle, color: "#DC2626", bg: "#FEE2E2" };
  return { Icon: FileText, color: "#5C6E64", bg: "#F8FAFC" };
};

const RANGE_OPTIONS = [
  { key: "today", label: "اليوم", days: 1 },
  { key: "3d", label: "3 أيام", days: 3 },
  { key: "7d", label: "أسبوع", days: 7 },
  { key: "30d", label: "30 يوم", days: 30 },
  { key: "all", label: "الكل", days: null },
];

export default function DailyLogTab() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("today");
  const [q, setQ] = useState("");
  const [markingAll, setMarkingAll] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/activity-log`, { params: { limit: 500 } });
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch (e) {
      toast.error("فشل تحميل الحركة اليومية");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const markAllRead = async () => {
    setMarkingAll(true);
    try {
      await axios.post(`${API}/admin/activity-log/mark-read`, { all: true });
      toast.success("تم وضع علامة مقروء على كل السجلات");
      load();
    } catch { toast.error("فشل"); }
    finally { setMarkingAll(false); }
  };

  const [restoring, setRestoring] = useState("");
  const restoreOrder = async (it) => {
    const inv = it.meta?.invoice_number || "";
    if (!window.confirm(`استرجاع الفاتورة #${inv}؟\nترجع للزبون نفس ما كانت، وتنخصم موادها من المخزن ويرجع الدين.`)) return;
    setRestoring(it.id);
    try {
      await axios.post(`${API}/admin/orders/${it.resource_id}/restore-deleted`);
      toast.success(`تم استرجاع الفاتورة #${inv}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل الاسترجاع"); }
    finally { setRestoring(""); }
  };

  const removeEntry = async (id) => {
    try {
      await axios.delete(`${API}/admin/activity-log/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch { toast.error("فشل الحذف"); }
  };

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    const opt = RANGE_OPTIONS.find((o) => o.key === range);
    const cutoffIso = opt && opt.days ? new Date(Date.now() - opt.days * 86400000).toISOString() : null;
    return items.filter((it) => {
      if (cutoffIso && (it.created_at || "") < cutoffIso) return false;
      if (!t) return true;
      const blob = `${it.summary || ""} ${it.actor_name || ""} ${it.action || ""} ${it.resource_type || ""}`.toLowerCase();
      return blob.includes(t);
    });
  }, [items, range, q]);

  const grouped = useMemo(() => {
    const out = {};
    filtered.forEach((it) => {
      const day = fmtDay(it.created_at);
      out[day] = out[day] || [];
      out[day].push(it);
    });
    return out;
  }, [filtered]);

  const dayKeys = Object.keys(grouped).sort().reverse();

  return (
    <div className="space-y-3" dir="rtl" data-testid="daily-log-tab">
      {/* Header */}
      <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-6 h-6" />
            <div>
              <h3 className="font-bold text-lg">الحركة اليومية</h3>
              <p className="text-xs opacity-90">سجل شامل لكل ما يحدث في النظام — مرتّب حسب التاريخ</p>
            </div>
          </div>
          <div className="text-left">
            <p className="text-[10px] opacity-80">غير مقروء</p>
            <p className="text-2xl font-black" data-testid="daily-log-unread-count" dir="ltr">{unread}</p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] space-y-2">
        <div className="flex flex-wrap gap-1.5" data-testid="daily-log-ranges">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              onClick={() => setRange(opt.key)}
              data-testid={`range-${opt.key}`}
              className={`text-xs font-bold py-1.5 px-3 rounded-full transition-colors ${
                range === opt.key
                  ? "bg-[#1E3A5F] text-white"
                  : "bg-[#F3F6F4] text-[#5C6E64] hover:bg-[#E4EBE6]"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative flex-1">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="ابحث في الحركة..."
              data-testid="daily-log-search"
              className="w-full pr-9 pl-4 py-2 rounded-full bg-[#F8FAFC] border-2 border-[#E4EBE6] focus:border-[#3B82F6] focus:outline-none text-sm"
            />
          </div>
          <button
            onClick={load}
            data-testid="daily-log-refresh"
            className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] p-2 rounded-full"
            title="تحديث"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={markAllRead}
            disabled={markingAll || unread === 0}
            data-testid="daily-log-mark-all-read"
            className="bg-[#7ED321] hover:bg-[#6cb91d] disabled:opacity-50 text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1"
          >
            <CheckCheck className="w-3.5 h-3.5" /> الكل مقروء
          </button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : dayKeys.length === 0 ? (
        <div className="bg-white border-2 border-dashed rounded-2xl p-10 text-center text-[#5C6E64]">
          <Activity className="w-10 h-10 mx-auto opacity-30 mb-2" />
          لا توجد حركة في هذه الفترة
        </div>
      ) : (
        dayKeys.map((day) => (
          <div key={day}>
            <div className="flex items-center gap-2 mb-1.5">
              <p className="text-xs font-bold text-[#1E3A5F] bg-[#E0F2FE] px-3 py-0.5 rounded-full inline-flex items-center gap-1" dir="ltr">
                <Calendar className="w-3 h-3" /> {day}
              </p>
              <span className="text-[10px] text-[#5C6E64]">({grouped[day].length})</span>
            </div>
            <div className="space-y-1.5">
              {grouped[day].map((it) => {
                const { Icon, color, bg } = styleFor(it);
                return (
                  <div
                    key={it.id}
                    data-testid={`daily-log-row-${it.id}`}
                    className={`relative rounded-xl p-2.5 flex items-start gap-2.5 border ${it.read ? "border-[#E4EBE6]" : "border-[#F59E0B]"} bg-white`}
                  >
                    <div className="rounded-full p-2 shrink-0" style={{ backgroundColor: bg }}>
                      <Icon className="w-4 h-4" style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-[#111814] leading-tight whitespace-pre-wrap break-words">{it.summary || "—"}</p>
                      <p className="text-[10px] text-[#5C6E64] mt-0.5">
                        <span className="font-bold">{it.actor_name || "—"}</span>
                        <span className="mx-1.5">·</span>
                        <span dir="ltr">{fmtTime(it.created_at)}</span>
                        {it.resource_type && <><span className="mx-1.5">·</span><span className="text-[#94A3B8]">{it.resource_type}</span></>}
                      </p>
                    </div>
                    {!it.read && <span className="absolute top-2 left-2 w-2 h-2 rounded-full bg-[#F59E0B] animate-pulse" />}
                    {it.action === "permanent_delete" && it.resource_type === "order" && it.meta?.can_restore && (
                      it.meta?.restored ? (
                        <span data-testid={`daily-log-restored-${it.id}`} className="text-[10px] font-bold text-emerald-700 bg-emerald-50 rounded-full px-2 py-1 shrink-0">✓ مسترجعة</span>
                      ) : (
                        <button
                          onClick={() => restoreOrder(it)}
                          disabled={restoring === it.id}
                          data-testid={`daily-log-restore-${it.id}`}
                          className="text-[11px] font-black text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-full px-3 py-1.5 shrink-0 flex items-center gap-1 transition-colors"
                        >
                          {restoring === it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                          استرجاع
                        </button>
                      )
                    )}
                    <button
                      onClick={() => removeEntry(it.id)}
                      data-testid={`daily-log-delete-${it.id}`}
                      className="text-[#E85D45] hover:bg-[#FEE2E2] rounded p-1.5 shrink-0"
                      title="حذف من السجل"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
